import Button from '@atoms/Button/Button';
import { ITabbedFeatureContent } from '@models/ITabbedFeatureContent';
import truncateText from '@utils/helpers/truncateText';
import useMediaQuery from '@utils/hooks/useMediaQuery';
import useResizeObserver from '@utils/hooks/useResizeObserver';
import classNames from 'classnames';
import { AnimatePresence, Variants, motion } from 'framer-motion';
import { useState } from 'react';

interface Props {
  readonly tab: ITabbedFeatureContent;
  readonly isActive: boolean;
  readonly index: number;
  readonly dataTestId: string;
  readonly showMoreLabel: string;
  readonly showLessLabel: string;
  readonly onSetActiveIndex: (activeIndex: number) => void;
}

const containerVariants: Variants = {
  enter: {
    height: 'auto',
    transition: {
      when: 'beforeChildren',
      staggerChildren: 0.1,
      duration: 0.3,
      ease: 'easeInOut',
    },
  },
  exit: { height: 0 },
};

const childVariants: Variants = {
  enter: {
    y: 0,
    opacity: 1,
    transition: {
      duration: 0.2,
      ease: 'easeInOut',
    },
  },
  exit: {
    y: 10,
    opacity: 0,
    transition: {
      duration: 0.2,
      ease: 'easeInOut',
    },
  },
};

const TabbedFeatureSection = ({
  tab,
  isActive,
  index,
  dataTestId,
  showMoreLabel,
  showLessLabel,
  onSetActiveIndex,
}: Props) => {
  const isBelowXXLScreen = useMediaQuery(1920);
  const textLineHeight = isBelowXXLScreen ? 23 : 24;
  const textCutOff = 5 * textLineHeight;
  const [textRef, { scrollHeight: textContentHeight }] = useResizeObserver();
  const [isTextExpanded, setIsTextExpanded] = useState(false);
  const isTextExpandable = textContentHeight > textCutOff;
  const hasCTALink = tab.cta?.url && tab.cta.name;

  const plainText =
    tab.text && tab.text?.length > 180 && hasCTALink
      ? truncateText(tab.text, 180, '...')
      : tab.text;

  return (
    <motion.div
      className={classNames(
        '-ml-px flex grow cursor-pointer flex-col justify-start border-l pl-4 text-left first:pl-4 xl:pl-10 xl:first:pl-10',
        isActive ? 'border-yellow' : 'border-grey-500'
      )}
    >
      <div role="tablist">
        {tab.title && (
          <button
            role="tab"
            key={tab.title}
            aria-controls={`tabpanel-${index}`}
            tabIndex={0}
            aria-selected={isActive}
            className={classNames(
              'h4 text-left font-light hover:text-white',
              isActive ? 'text-white' : 'text-grey-500'
            )}
            onClick={() => {
              onSetActiveIndex(index);

              if (!isActive) {
                setIsTextExpanded(false);
              }
            }}
            data-testid={`${dataTestId}-Button`}
          >
            {tab.title}
          </button>
        )}
      </div>

      <AnimatePresence mode="wait">
        {isActive && (
          <motion.div
            id={`tabpanel-${index}`}
            aria-labelledby={`tab-${index}`}
            variants={containerVariants}
            initial="exit"
            animate="enter"
            exit="exit"
            key="parent"
            role="tabpanel"
          >
            {(tab.text || tab.textNew) && (
              <motion.div
                variants={childVariants}
                className="flex flex-col items-start"
                data-testid={`${dataTestId}-Text`}
              >
                <motion.div
                  ref={textRef}
                  className="body-three overflow-hidden text-grey-500"
                  {...(hasCTALink
                    ? {}
                    : {
                        initial: { height: isTextExpandable ? textCutOff : 0 },
                        animate: isTextExpanded ? 'expanded' : 'collapsed',
                        variants: {
                          expanded: { height: 'auto' },
                          collapsed: {
                            height: isTextExpandable ? textCutOff : 'auto',
                          },
                        },
                      })}
                  dangerouslySetInnerHTML={{
                    __html: (tab.textNew || plainText) ?? '',
                  }}
                />

                {isTextExpandable && !hasCTALink && (
                  <button
                    className="shrink-0 text-grey-500 underline"
                    onClick={() => {
                      setIsTextExpanded(!isTextExpanded);
                    }}
                    data-testid="Banner-Notification-Show-More/Less"
                  >
                    <strong>
                      {isTextExpanded ? showLessLabel : showMoreLabel}
                    </strong>
                  </button>
                )}
              </motion.div>
            )}

            {hasCTALink && (
              <motion.div
                key="button"
                className="mt-7 mb-1"
                variants={childVariants}
              >
                <Button link={tab.cta} dataTestId={`${dataTestId}-CTA`} />
              </motion.div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default TabbedFeatureSection;
